import AdvanceFilterDialog from "@components/advance-filter-dialog";
import React, { useRef, useState } from "react";
import {
  Grid,
  Box,
  Button,
  Badge as MuiBadge,
  styled as muiStyled,
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { useWidth } from "custom-hooks/use-width.hook";
import { Form, Formik, FormikConfig, FormikProps } from "formik";
import { useTranslations } from "next-intl";
import { useRouter } from "next/router";
import { CPagesPath } from "shared/models/const/pages-path";
import { useIntlPropertyTypes } from "shared/models/const/property-types";
import { getRealestateDetails } from "api/realestate.request";
import { ModalMessage } from "@components/message-modal";

import RadioGroupStatus from "./RadioGroupStatus";
import AutoCompleteField, {
  AutoCompleteField as DefaultAutoComplete,
} from "../auto-complete-field";
import { FormConfig, makeFilterObject } from "./util/form.util";
import useCities from "custom-hooks/use-cities.hook";
import sortCities from "@helpers/cities.helper";
import { debounce } from "lodash";

function BuySellFilter(props: any) {
  const t = useTranslations("buy-sell-filter");
  const translationNotFound = useTranslations("buy-sell-filter.notFound");
  const translation = useTranslations("realestateList.typeFilters");
  const width = useWidth();
  const isMobile = ["xs", "sm"].includes(width);
  const ref = useRef<FormikProps<any>>(null);
  const refButton = useRef<any>();
  const router = useRouter();
  const [search, onSearch] = useState("");
  const [open, setOpen] = useState<boolean>(false);

  const propertyTypes = useIntlPropertyTypes();
  const isCollection = router.asPath.includes(CPagesPath.es.collection);

  const propertyDetailRedirect = isCollection
    ? CPagesPath.es.collection_property_detail
    : CPagesPath.es.property_detail;

  const collectionProperties = [
    translation("house"),
    translation("apartment"),
    translation("tourist"),
    translation("penthouse"),
    translation("project"),
  ];

  const collectionTypes = propertyTypes.filter((e) =>
    collectionProperties.includes(e.name)
  );

  const types = props.collection ? collectionTypes : propertyTypes;

  const handleSearch = React.useCallback((e: any) => {
    const values = e.target.value;
    onSearch(values);
  }, []);

  const handleOpenModal = () => {
    setOpen(true);
  };

  const handleCloseModal = () => {
    setOpen(false);
  };

  const citiesList = useCities(
    {
      q: search,
    },
    500
  );

  const getRealestateRequest = useMutation(
    ["detail-realstate"],
    (realestateId: number) => {
      return getRealestateDetails(realestateId);
    }
  );

  const submitValues = debounce((value: any) => {
    const realestateId = parseInt(search);

    if (Boolean(realestateId)) {
      getRealestateRequest
        .mutateAsync(realestateId)
        .then((r) => {
          router.push({
            pathname: propertyDetailRedirect(r.data),
            query: { ...router.query, ...props.params },
          });
        })
        .catch(() => handleOpenModal());
    } else {
      const route =
        props.redirect ??
        (router.pathname.length > 1 ? router.pathname : "") +
          CPagesPath.es.properties;

      const params = makeFilterObject({ ...value, ...props.params });
      router.push({
        pathname: route,
        query: params,
      });
    }
  }, 500);

  const handleLocationChange = (e: any, newValue: any) => {
    // if enter was pressed
    if (e.keyCode === 13 && Boolean(parseInt(search))) {
      submitValues({});
    } else if (ref.current) {
      if (newValue.length == 2) {
        ref.current.setFieldValue("locations", [newValue[1]]);
        return;
      }
      ref.current.setFieldValue("locations", newValue);
    }
  };

  const isLoading = () => {
    if (Boolean(parseInt(search))) {
      return false;
    }

    return citiesList.isLoading;
  };

  const handleModal = () => {
    refButton.current.click();
  };

  const Cities_Options = citiesList.data ? sortCities(citiesList.data) : [];

  const Cities_noOptionsText = !(search?.length > 2)
    ? t("searchHelp")
    : t("searchHelpSD");
  const initialValues = props.values ? props.values : FormConfig.initialValues;
  const handleSubmit = props.onSubmit ? props.onSubmit : submitValues;
  const Cities_getOptionLabel = (option: any) => option?.description;
  const Cities_renderOptions = (props: any, option: any) => (
    <ItemElementLabel {...props} {...option}></ItemElementLabel>
  );

  const FormikConfig: FormikConfig<any> = {
    innerRef: ref,
    initialValues: initialValues,
    onSubmit: handleSubmit,
    enableReinitialize: true,
  };

  return (
    <FilterContent className={props.className}>
      <Formik {...FormikConfig}>
        {(formikProp) => (
          <CoverSearch id="cover_search">
            {!isMobile && (
              <RadioGroupStatus
                name="businessTypes"
                isCollection={props.collection}
              ></RadioGroupStatus>
            )}
            <CoverSearchBody
              className="content-body"
              isCollection={props.collection}
            >
              <Grid container>
                <Grid
                  item
                  xs={4}
                  className="typeProperty-autocomplete-container"
                >
                  <Label>{t("youLookingFor")}</Label>
                  <CoverAutoComplete
                    name="typeProperty"
                    limitTags={1}
                    multiple={true}
                    className="typeProperty-autocomplete"
                    placeholder={t("typePlaceholder")}
                    getOptionLabel={(option: any) => option?.description}
                    options={types.map((x) => ({
                      ...x,
                      description: x.name,
                      id: x.id,
                    }))}
                  ></CoverAutoComplete>
                </Grid>

                <div>
                  <DividerSearch></DividerSearch>
                </div>

                <Grid
                  item
                  xs
                  onClick={(e: any) => {
                    if (isMobile) {
                      e.preventDefault();
                      handleModal();
                    }
                  }}
                >
                  <Label>{t("where")}</Label>
                  <LocationAutoComplete
                    name="locations"
                    limitTags={1}
                    multiple={true}
                    INPUT_DELAY={600}
                    loading={isLoading()}
                    options={Cities_Options}
                    onInputChange={handleSearch}
                    className="city-autocomplete"
                    onChange={handleLocationChange}
                    renderOption={Cities_renderOptions}
                    value={formikProp.values.locations}
                    noOptionsText={Cities_noOptionsText}
                    freeSolo={Boolean(parseInt(search))}
                    placeholder={t("locationPlaceholder")}
                    getOptionLabel={Cities_getOptionLabel}
                  />
                </Grid>
              </Grid>

              <ButtonSubmit type="submit" isCollection={props.collection}>
                <img
                  src="/icons/loupe_remax_rd.svg"
                  alt="Loupe icon"
                  width="35px"
                  height="35px"
                />
              </ButtonSubmit>
            </CoverSearchBody>
            {!props.collection && (
              <AdvanceFilterDialog
                handleReset={formikProp.handleReset}
                handleSubmit={formikProp.handleSubmit}
                refButton={refButton}
              >
                <ButtonFilter type="button" isCollection={props.collection}>
                  <img
                    src="/icons/filter_remax_rd.svg"
                    alt="Filter icon"
                    width="11px"
                    height="11px"
                  />{" "}
                  {t("AdvanceFilters")}
                  {props.filtersCount > 0 && (
                    <Badge>{props.filtersCount}</Badge>
                  )}
                </ButtonFilter>
              </AdvanceFilterDialog>
            )}
          </CoverSearch>
        )}
      </Formik>
      <ModalMessage
        title={translationNotFound("title")}
        body={translationNotFound("body")}
        variant="error"
        open={open}
        handleClose={handleCloseModal}
      />
    </FilterContent>
  );
}

export default BuySellFilter;

const ItemElementLabel = (props: any) => {
  const { citie, description, isSector, ...rest } = props;
  const Wrap = description?.length > 8 ? "wrap" : "";

  return (
    <Box
      component="li"
      sx={{
        width: "100%",
        display: "flex",
        "&.MuiAutocomplete-option.Mui-focused": {
          backgroundColor: "transparent",
          "&:hover span": {
            color: "white",
          },
        },
        "& .noWrap": {
          whiteSpace: "nowrap",
        },
        "& span": {
          color: "var(--color-brand-blue-main)",
          fontWeight: "bold",
          marginRight: "5px",
        },
        "& .isCity, .isSector": {
          color: "var(--color-brand-blue-main)",
        },
        "& .description": {
          color: "#989694",
          lineHeight: "20px",
        },
        "@media screen and (max-width: 450px)": {
          flexDirection: "column",
          alignItems: "start",
          whiteSpace: "nowrap",
          textAlign: "left",
          fontSize: "13px",
          "& .isCity": {
            width: "100%",
          },
          "& .description": {
            textAlign: "left",
            fontWeight: 400,
            width: "100%",
          },
        },
      }}
      {...rest}
    >
      <span className={`noWrap isCity`}>{description}</span>
    </Box>
  );
};

const Label = muiStyled("span")({
  fontSize: "0.9rem",
  fontWeight: 600,
  cursor: "pointer",
  color: "var(--color-text-primary)",
});

const ButtonSubmit = muiStyled(Button)(
  ({ isCollection }: { isCollection: boolean }) => ({
    backgroundColor: "black",
    ":hover": {
      backgroundColor: "var(--color-brand-red-tertiary)",
    },
    border: "none",
    outline: "none",
    padding: "10px 20px",
    borderRadius: "15px",
    position: "absolute",
    right: "-10px",
    alignSelf: "center",
    cursor: "pointer",
    ...(isCollection && {
      backgroundColor: "var(--color-brand-blue-main)",
      "&:hover": {
        backgroundColor: "var(--color-brand-blue-main)",
      },
    }),
    "& img": {
      verticalAlign: "middle",
    },
    "@media screen and (max-width: 767px)": {
      "& img": {
        width: "20px",
      },
    },
    "@media screen and (max-width: 450px)": {
      right: "-10px",
    },
  })
);

const DividerSearch = muiStyled(Grid)({
  borderLeft: "1px solid var(--color-brand-blue-main)",
  height: "49px",
  width: "1px",
  margin: "auto 10px",
  marginTop: "5px",
  "@media screen and (max-width: 740px)": {
    display: "none",
  },
});

const ButtonFilter = muiStyled(Button)(
  ({ isCollection }: { isCollection: boolean }) => ({
    position: "absolute",
    top: "100%",
    right: "80px",

    backgroundColor: "var(--color-brand-red-main)",
    ":hover": {
      backgroundColor: "var(--color-brand-red-tertiary)",
    },
    ...(isCollection && {
      backgroundColor: "var(--color-brand-blue-main)",
      "&:hover": {
        backgroundColor: "var(--color-brand-blue-main)",
      },
    }),
    textTransform: "none",
    color: "var(--color-text-white)",
    padding: "10px 22px",
    border: "none",
    outline: "none",
    borderRadius: "0 0 10px 10px",
    fontSize: "0.85rem",
    cursor: "pointer",
    fontWeight: 700,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "10px",
    "& img": {
      verticalAlign: "middle",
    },
    "@media screen and (max-width: 767px)": {
      marginBottom: "20px",
    },
  })
);

const CoverSearchBody = muiStyled(Box)(
  ({ isCollection }: { isCollection: boolean }) => ({
    backgroundColor: "#fff",
    position: "relative",
    zIndex: 10,
    padding: "8px 38px 10px 33px",
    borderRadius: "50px",
    ...(isCollection && {
      borderRadius: "15px",
    }),
    display: "flex",
    height: "75px",
    "@media screen and (max-width: 767px)": {
      flexWrap: "wrap",
      padding: "15px 30px",
      height: "auto",
    },
    "@media screen and (max-width: 450px)": {
      borderRadius: "10px",
      padding: "15px 15px",
    },
  })
);

const CoverSearch = muiStyled(Form)({
  position: "relative",
  width: "840px",
  "@media screen and (max-width: 991px)": {
    width: "700px",
  },
  "@media screen and (max-width: 891px)": {
    width: "600px",
  },
  "@media screen and (max-width: 740px)": {
    width: "570px",
  },
  "@media screen and (max-width: 732px)": {
    width: "520px",
  },
  "@media screen and (max-width: 630px)": {
    width: "440px",
  },
  "@media screen and (max-width: 450px)": {
    width: "340px",
  },
});

const CoverAutoComplete = muiStyled(AutoCompleteField)({
  width: "100%",
  border: "none",
  outline: "none",
  padding: "0px",
  "& .MuiPaper-root.MuiAutocomplete-noOptions": {
    textTransform: "initial !important",
  },
  "& .MuiInputBase-fullWidth.MuiInputBase-root": {
    flexWrap: "nowrap",
    "&.Mui-focused": {
      position: "absolute",
      flexWrap: "wrap",
    },
  },
  "& .MuiInputBase-root": {
    marginTop: "-3px",
    padding: "0px !important",
    backgroundColor: "white",
    "& *": {
      border: "none !important",
    },
  },
  "& .MuiAutocomplete-tag": {
    maxWidth: "110px",
    backgroundColor: "var(--color-brand-blue-main)",
    color: "white",
    fontWeight: 500,
  },
  "& .MuiChip-deleteIcon": {
    color: "white !important",
  },
  "& span.MuiAutocomplete-tagSizeMedium": {
    borderRadius: "100%",
    width: "20px",
    height: "20px",
    fontSize: "10px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  "& .Mui-focused .MuiAutocomplete-tag": {
    maxWidth: "calc(100% - 6px)",
  },
  "& .city-autocomplete": {
    width: "100%",
  },
});

const LocationAutoComplete = muiStyled(DefaultAutoComplete)({
  width: "100%",
  border: "none",
  outline: "none",
  padding: "0px",
  "& .MuiPaper-root.MuiAutocomplete-noOptions": {
    textTransform: "initial !important",
  },
  "& .MuiInputBase-fullWidth.MuiInputBase-root": {
    flexWrap: "nowrap",
    "&.Mui-focused": {
      position: "absolute",
      flexWrap: "wrap",
    },
  },
  "& .MuiInputBase-root": {
    marginTop: "-3px",
    padding: "0px !important",
    backgroundColor: "white",
    "& *": {
      border: "none !important",
    },
  },
  "& .MuiAutocomplete-tag": {
    backgroundColor: "var(--color-brand-blue-main)",
    color: "white",
    fontWeight: 500,
  },
  "& .MuiChip-deleteIcon": {
    color: "white !important",
  },
  "& span.MuiAutocomplete-tagSizeMedium": {
    borderRadius: "100%",
    width: "20px",
    height: "20px",
    fontSize: "10px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  "& .Mui-focused .MuiAutocomplete-tag": {
    maxWidth: "calc(100% - 6px)",
  },
  "& .city-autocomplete": {
    width: "100%",
  },
});

const FilterContent = muiStyled(Box)({
  margin: "auto",
  "@media screen and (max-width: 740px)": {
    "& .typeProperty-autocomplete-container": {
      display: "none",
    },
  },
});

const Badge = muiStyled(MuiBadge)({
  minWidth: "20px",
  minHeight: "20px",
  borderRadius: "10px",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  position: "absolute",
  color: "var(--color-brand-bg-secondary)",
  backgroundColor: "var(--color-brand-blue-main)",
  fontSize: "0.75rem",
  fontWeight: 600,
  top: "18px",
  right: "0",
});
